import React from 'react';

import { di } from 'react-magnetic-di';

import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { useChatContextEditorContext } from '@atlassian/conversation-assistant-store/controllers/chat-context/store';
import { MessageFooterButtons as PlatformMessageFooterButtons } from '@atlassian/rovo-platform-ui-components/message-footer-buttons';

import { POLLINATOR_TEST_IDS } from '../../constants/POLLINATOR_TEST_IDS';

import { type FeedbackType } from './feedback-confirmation-banner/FeedbackType';
import { InsertReplaceButton, type InsertReplaceButtonProps } from './insert-replace';
import { ShareUpToMessageButton } from './share-response/ShareUpToMessageButton';

type Props = {
	onThumbClick: (value: boolean) => void;
	hideThumbs: boolean;
	onCopyClick: () => void;
	insertReplaceContent: InsertReplaceButtonProps['content'] | undefined;
	selectedAgentId: InsertReplaceButtonProps['agentId'];
	onClickInsertReplace: InsertReplaceButtonProps['onClick'];
	hideInsertButton?: boolean;
	showDebugButton?: boolean;
	onClickDebug?: () => void;
	feedbackType?: FeedbackType | null;
	markdownData?: InsertReplaceButtonProps['markdownData'];
	messageId?: string;
};

export const MessageFooterButtons = ({
	hideThumbs,
	onThumbClick,
	onCopyClick,
	insertReplaceContent,
	selectedAgentId,
	onClickInsertReplace,
	hideInsertButton = false,
	showDebugButton = false,
	onClickDebug,
	feedbackType,
	markdownData,
	messageId,
}: Props): JSX.Element => {
	di(useChatContextEditorContext);
	const [editorContext] = useChatContextEditorContext();
	const showInsertButton =
		!hideInsertButton &&
		!!insertReplaceContent &&
		!!editorContext?.document &&
		!editorContext?.isViewMode;

	const { sendAnalyticsEvent } = useAnalytics();

	return (
		<PlatformMessageFooterButtons
			hideThumbs={hideThumbs}
			feedbackType={feedbackType}
			thumbsTestId={POLLINATOR_TEST_IDS.THUMBS_UP_BUTTON}
			onThumbClick={(value, event) => {
				sendAnalyticsEvent(
					event.update({ actionSubjectId: value ? 'goodAnswerButton' : 'badAnswerButton' }),
				);
				onThumbClick(value);
			}}
			onCopyClick={(event) => {
				sendAnalyticsEvent(event.update({ actionSubjectId: 'copyResponseButton' }));
				onCopyClick();
			}}
			showDebugButton={showDebugButton}
			onClickDebug={(event) => {
				sendAnalyticsEvent(event.update({ actionSubjectId: 'debugButton' }));
				onClickDebug?.();
			}}
			insertSlot={
				showInsertButton ? (
					<InsertReplaceButton
						content={insertReplaceContent}
						agentId={selectedAgentId}
						onClick={onClickInsertReplace}
						markdownData={markdownData}
					/>
				) : undefined
			}
			shareSlot={messageId ? <ShareUpToMessageButton messageId={messageId} /> : undefined}
		/>
	);
};
